import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TbMessages } from "react-icons/tb";
import { FiSearch } from "react-icons/fi";
import { isToday, isYesterday, subDays, isWithinInterval } from "date-fns";
import {
  SubSidebarWrapper,
  SubSidebarHeader,
  SubSidebarSearch,
  SubSidebarChatList,
  SubSidebarChatItem,
  SubSidebarNewChatButton,
  SubSidebarDateSeparator,
} from "./Styles";
import { useChat } from "../../Common/Hooks/useChat";
import { Chat as ChatDTO } from "../../Common/Types-DTOs/Chat";
import { isErr } from "../../Common/Utils/Result";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../../Components/Notifications";
import { useAuth } from "../../Common/Hooks/useAuth";
import { formatChatGroupLabel } from "../../Common/Utils/dateUtils";
import { useSearch } from "../../Common/Hooks/useSearch";
import { Highlight } from "@search-components";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const getDateKey = (date: Date): string => {
  const now = new Date();
  const chatDate = new Date(date);

  if (isToday(chatDate)) return "today";
  if (isYesterday(chatDate)) return "yesterday";

  const weekAgo = subDays(now, 7);
  if (isWithinInterval(chatDate, { start: weekAgo, end: now })) return "week";

  const monthAgo = subDays(now, 30);
  if (isWithinInterval(chatDate, { start: monthAgo, end: now })) return "month";

  return "older";
};

interface CopilotSubSidebarProps {
  isVisible: boolean;
  onNewChat: () => void;
}

export const CopilotSubSidebar = ({ isVisible, onNewChat }: CopilotSubSidebarProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [chats, setChats] = useState<ChatDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { getAllChats } = useChat();
  const { isLoggedIn } = useAuth();
  const isMountedRef = useRef(true);
  const { search, results: searchResults, loading: isSearching, clear: clearSearch } = useSearch();

  useEffect(() => {
    isMountedRef.current = true;

    const loadChats = async () => {
      setIsLoading(true);
      const response = await getAllChats();

      if (!isMountedRef.current) {
        return;
      }

      if (isErr(response)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("common.sidebar.copilot.errors.loadChatsFailed"))
        );
        setIsLoading(false);
        return;
      }

      // La API devuelve 'conversations', no 'chats'
      setChats(response.value.conversations || []);
      setIsLoading(false);
    };

    if (isVisible && isLoggedIn) {
      loadChats();
    } else if (!isLoggedIn) {
      setIsLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [isVisible, location.pathname, isLoggedIn, getAllChats]);

  // Debounce search input: when the user types, hit the FTS endpoint after a pause.
  // FTS searches across ALL messages (not just the first), with Spanish/English
  // stemming and prefix matching, so "res" finds "respuesta" inside Mike's replies.
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      clearSearch();
      return;
    }
    const timer = setTimeout(() => {
      search({ q: trimmed, type: "chats", limit: 50 });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery, search, clearSearch]);

  const filteredChats = useMemo(() => {
    if (searchQuery.trim().length < MIN_SEARCH_LENGTH) return chats;
    if (!searchResults) return [];
    // Preserve FTS ranking by iterating result order, keeping only chats present locally.
    const chatById = new Map(chats.map((c) => [c.id, c]));
    return searchResults.chats.results
      .map((r) => chatById.get(r.chatId))
      .filter((c): c is ChatDTO => c !== undefined);
  }, [chats, searchQuery, searchResults]);

  const isSearchActive = searchQuery.trim().length >= MIN_SEARCH_LENGTH;

  // Group chats by date — only used when not searching. When searching, we render
  // a flat list preserving FTS rank order.
  const groupedChats = useMemo(() => {
    if (isSearchActive) return [];

    const groups: { dateKey: string; dateLabel: string; chats: ChatDTO[] }[] = [];

    // Sort chats by last message date (most recent first)
    const sortedChats = [...filteredChats].sort((a, b) => {
      const dateA = a.messages && a.messages.length > 0
        ? new Date(a.messages[a.messages.length - 1].creationDate).getTime()
        : 0;
      const dateB = b.messages && b.messages.length > 0
        ? new Date(b.messages[b.messages.length - 1].creationDate).getTime()
        : 0;
      return dateB - dateA;
    });

    sortedChats.forEach((chat) => {
      if (chat.messages && chat.messages.length > 0) {
        const lastMessage = chat.messages[chat.messages.length - 1];
        const dateKey = getDateKey(lastMessage.creationDate);
        const existingGroup = groups.find(g => g.dateKey === dateKey);

        if (existingGroup) {
          existingGroup.chats.push(chat);
        } else {
          groups.push({
            dateKey,
            dateLabel: formatChatGroupLabel(lastMessage.creationDate),
            chats: [chat],
          });
        }
      }
    });

    // Sort groups by priority (today first, then yesterday, etc.)
    const groupOrder = ['today', 'yesterday', 'week', 'month', 'older'];
    groups.sort((a, b) => groupOrder.indexOf(a.dateKey) - groupOrder.indexOf(b.dateKey));

    return groups;
  }, [filteredChats, isSearchActive]);

  // Map chatId -> snippet so the sidebar can show the matching excerpt under each title.
  const snippetsByChatId = useMemo(() => {
    if (!searchResults) return new Map<string, string>();
    return new Map(searchResults.chats.results.map((r) => [r.chatId, r.snippet]));
  }, [searchResults]);

  if (!isVisible) return null;

  const getChatTitle = (chat: ChatDTO) => {
    if (chat.messages && chat.messages.length > 0) {
      const firstMessage = chat.messages[0].text || "";
      return firstMessage.length > 50 ? firstMessage.substring(0, 50) + "..." : firstMessage;
    }
    return t("common.sidebar.copilot.untitledChat");
  };

  const getLastMessage = (chat: ChatDTO) => {
    if (chat.messages && chat.messages.length > 0) {
      const raw = chat.messages[chat.messages.length - 1].text || "";
      return raw
        .replace(/\[BR\]/g, " ")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/(?<!\w)_(.+?)_(?!\w)/g, "$1")
        .replace(/~(.+?)~/g, "$1")
        .replace(/`(.+?)`/g, "$1")
        .replace(/\s{2,}/g, " ")
        .trim();
    }
    return "";
  };

  const currentChatId = location.pathname.split("/chat/")[1];

  return (
    <SubSidebarWrapper>
      <SubSidebarHeader>
        <SubSidebarNewChatButton onClick={onNewChat}>
          <TbMessages size={18} />
          {t("common.sidebar.copilot.newChatButton")}
        </SubSidebarNewChatButton>
        <SubSidebarSearch>
          <FiSearch size={16} />
          <input
            type="text"
            placeholder={t("common.sidebar.copilot.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SubSidebarSearch>
      </SubSidebarHeader>

      <SubSidebarChatList>
        {isLoading ? (
          <div
            style={{
              padding: "2rem 1rem",
              textAlign: "center",
              color: "#999",
              fontSize: "0.875rem",
            }}
          >
            {t("common.sidebar.copilot.loadingChats")}
          </div>
        ) : isSearchActive && isSearching && !searchResults ? (
          <div
            style={{
              padding: "2rem 1rem",
              textAlign: "center",
              color: "#999",
              fontSize: "0.875rem",
            }}
          >
            {t("common.sidebar.copilot.searching")}
          </div>
        ) : isSearchActive ? (
          filteredChats.length > 0 ? (
            filteredChats.map((chat) => {
              const isActive = chat.id === currentChatId;
              const snippet = snippetsByChatId.get(chat.id);
              return (
                <SubSidebarChatItem
                  key={chat.id}
                  $isActive={isActive}
                  onClick={() => navigate(`/chat/${chat.id}`)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "inherit",
                      lineHeight: "1.4",
                      marginBottom: "0.25rem"
                    }}>
                      {getChatTitle(chat)}
                    </div>
                    {snippet && (
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "inherit",
                          opacity: 0.7,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: "1.3",
                        }}
                      >
                        <Highlight html={snippet} />
                      </div>
                    )}
                  </div>
                </SubSidebarChatItem>
              );
            })
          ) : (
            <div
              style={{
                padding: "2rem 1rem",
                textAlign: "center",
                color: "#999",
                fontSize: "0.875rem",
              }}
            >
              {t("common.sidebar.copilot.noResults")}
            </div>
          )
        ) : groupedChats.length > 0 ? (
          groupedChats.map((group) => (
            <div key={group.dateKey}>
              <SubSidebarDateSeparator>{group.dateLabel}</SubSidebarDateSeparator>
              {group.chats.map((chat) => {
                const isActive = chat.id === currentChatId;
                return (
                  <SubSidebarChatItem
                    key={chat.id}
                    $isActive={isActive}
                    onClick={() => navigate(`/chat/${chat.id}`)}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "inherit",
                        lineHeight: "1.4",
                        marginBottom: "0.25rem"
                      }}>
                        {getChatTitle(chat)}
                      </div>
                      {getLastMessage(chat) && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "inherit",
                            opacity: 0.7,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: "1.3",
                          }}
                        >
                          {getLastMessage(chat)}
                        </div>
                      )}
                    </div>
                  </SubSidebarChatItem>
                );
              })}
            </div>
          ))
        ) : (
          <div
            style={{
              padding: "2rem 1rem",
              textAlign: "center",
              color: "#999",
              fontSize: "0.875rem",
            }}
          >
            {t("common.sidebar.copilot.emptyState")}
          </div>
        )}
      </SubSidebarChatList>
    </SubSidebarWrapper>
  );
};




